import React, { useEffect, useRef } from 'react'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import useSupport from '@verzekeringapp-mono/shared/src/hooks/use_support'
import { getAboutMembership as getAboutMembershipSelector } from '@verzekeringapp-mono/shared/src/state/selectors/support'
import usePreferencesController, { ThemePreferenceMode } from '~/hooks/use_preferences_controller'
import { preferenceRoutes, PreferenceRouteId } from './preferences_config'
import PreferenceToggle from './components/preference_toggle'

type PreferenceDetailScreenProps = {
  preference: PreferenceRouteId
}

const customerPanelHtml = '<p>Help je ons verbeteren? Meld je aan of af voor het Nh1816 klantenpanel. Je leest meer over ons klantenpanel op <a href="https://nh1816.nl/klantenpanel" target="_blank">nh1816.nl/klantenpanel</a></p>'

const displayOptions: Array<{ label: string; value: ThemePreferenceMode }> = [
  { label: 'Licht', value: 'light' },
  { label: 'Donker', value: 'dark' },
  { label: 'Toestelinstelling', value: 'system' }
]

const PreferenceDetailScreen: React.FC<PreferenceDetailScreenProps> = ({ preference }) => {
  const aboutMembership = useAppSelector(getAboutMembershipSelector)
  const { getAboutMembership } = useSupport()
  const {
    display,
    ensureZestAccessible,
    relationToggles,
    setCooperationMembership,
    setCustomerPanel,
    setZestMagazine
  } = usePreferencesController()
  const hasShownBlockedNotice = useRef(false)

  useEffect(() => {
    if (preference === 'cooperation-membership' && !aboutMembership) {
      void getAboutMembership()
    }
  }, [aboutMembership, getAboutMembership, preference])

  useEffect(() => {
    if (
      preference !== 'zest-magazine' ||
      !relationToggles ||
      relationToggles.cooperationMembership ||
      hasShownBlockedNotice.current
    ) {
      return
    }

    hasShownBlockedNotice.current = true
    ensureZestAccessible({ navigateOnBlock: true })
  }, [ensureZestAccessible, preference, relationToggles])

  const currentPreference = preferenceRoutes[preference]

  if (preference !== 'display' && !relationToggles) {
    return null
  }

  if (preference === 'zest-magazine' && !relationToggles?.cooperationMembership) {
    return null
  }

  return (
    <ScreenWrapper
      breadCrumbsOverride={[
        {
          path: '/mijn-profiel',
          label: 'Mijn profiel'
        },
        {
          path: '/mijn-profiel/voorkeuren',
          label: 'Voorkeuren'
        },
        {
          path: currentPreference.path,
          label: currentPreference.title
        }
      ]}
    >
      <div className='min-h-full w-full rounded bg-surface-page p-8'>
        <div className='lg:w-2/6 flex flex-col gap-6'>
          {preference === 'cooperation-membership' && relationToggles && (
            <>
              <div className='flex flex-col gap-4'>
                <p className='text-base font-bold'>Lidmaatschap coöperatie Nh1816</p>

                {aboutMembership && (
                  <div
                    className='html-content'
                    dangerouslySetInnerHTML={{ __html: aboutMembership }}
                  />
                )}
              </div>

              <div className='flex flex-col gap-2'>
                <PreferenceToggle
                  label='Lidmaatschap'
                  checked={relationToggles.cooperationMembership}
                  onChange={(value) => {
                    void setCooperationMembership(value)
                  }}
                />

                <p className='text-xs text-text-secondary'>Je wijziging wordt binnen 24 uur verwerkt.</p>
              </div>
            </>
          )}

          {preference === 'zest-magazine' && relationToggles && (
            <>
              <div className='flex flex-col gap-3'>
                <p className='text-base font-bold'>Zest Magazine</p>

                <p className='text-base text-text-primary'>
                  Zest Magazine geven wij samen met jouw adviseur eenmaal per jaar uit.
                </p>

                <p className='text-base text-text-primary'>
                  Ontvang je Zest Magazine liever niet? Schrijf je hieronder uit.
                </p>
              </div>

              <div className='flex flex-col gap-2'>
                <PreferenceToggle
                  label='Zest Magazine ontvangen'
                  checked={relationToggles.zestMagazine}
                  onChange={(value) => {
                    void setZestMagazine(value)
                  }}
                />

                <p className='text-xs text-text-secondary'>Je wijziging wordt binnen 24 uur verwerkt.</p>
              </div>
            </>
          )}

          {preference === 'customer-panel' && relationToggles && (
            <>
              <div className='flex flex-col gap-4'>
                <p className='text-base font-bold'>Klantenpanel</p>

                <div
                  className='html-content'
                  dangerouslySetInnerHTML={{ __html: customerPanelHtml }}
                />
              </div>

              <div className='flex flex-col gap-2'>
                <PreferenceToggle
                  label='Klantenpanel'
                  checked={relationToggles.customerPanel}
                  onChange={(value) => {
                    void setCustomerPanel(value)
                  }}
                />

                <p className='text-xs text-text-secondary'>Je wijziging wordt binnen 24 uur verwerkt.</p>
              </div>
            </>
          )}

          {preference === 'display' && (
            <>
              <div className='flex flex-col gap-3'>
                <p className='text-base font-bold text-text-primary'>Beeldscherm</p>

                <p className='text-base text-text-primary'>
                  Wil je een donkere of lichte achtergrond? Geef jouw voorkeur hier aan.
                </p>
              </div>

              <fieldset
                className='flex flex-col gap-6'
                aria-label='Beeldschermvoorkeur'
              >
                {displayOptions.map((option) => {
                  const checked = display.selectedMode === option.value

                  return (
                    <label
                      key={option.value}
                      className='flex cursor-pointer items-center gap-6 text-text-primary'
                    >
                      <input
                        type='radio'
                        name='display-mode'
                        value={option.value}
                        checked={checked}
                        className='sr-only peer'
                        onChange={() => {
                          display.setMode(option.value)
                        }}
                      />

                      <span className='flex h-8 w-8 items-center justify-center rounded-full border border-border-strong bg-surface-card peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-border-focus peer-focus-visible:outline-offset-2'>
                        {checked && <span className='h-[22px] w-[22px] rounded-full bg-action-primary-background' />}
                      </span>

                      <span className='text-lg'>{option.label}</span>
                    </label>
                  )
                })}
              </fieldset>
            </>
          )}
        </div>
      </div>
    </ScreenWrapper>
  )
}

export default PreferenceDetailScreen
